import React, { useCallback } from 'react'
import { StarFilled, StarOutlined } from '@ant-design/icons'
import { useMutation } from '@apollo/client'
import { Button, Table, Typography } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import dayjs from 'dayjs'
import { useHistory } from 'react-router-dom'
import { DELETE_FOURNISSEUR_MUTATION, FOURNISSEUR_FAVORI_MUTATION_ONE } from '../../apollo/mutation/favori.mutation'
import { INTERVENTIONS_ALL_QUERY } from '../../apollo/query/interventions.query'
import { COLOR, DATE_FORMAT } from '../../constants'
import { D4_Interventiondemande } from '../../types'

const FournisseurFavAction = ({
  typeequipementId,
  equipementId,
  intervention,
}: {
  typeequipementId: number
  equipementId: number
  intervention: D4_Interventiondemande
  trigramme: string
}) => {
  const [insert] = useMutation(FOURNISSEUR_FAVORI_MUTATION_ONE, {
    refetchQueries: [{ query: INTERVENTIONS_ALL_QUERY, variables: { typeequipementId } }],
  })
  const [remove] = useMutation(DELETE_FOURNISSEUR_MUTATION, {
    refetchQueries: [{ query: INTERVENTIONS_ALL_QUERY, variables: { typeequipementId } }],
  })

  const favoris = intervention.fournisseur?.fournisseur_favori_equipements.map((fav) => fav.equipement.id)

  const isFav = favoris?.includes(equipementId)

  const handleClick = useCallback(() => {
    if (isFav) {
      const favId = intervention.fournisseur?.fournisseur_favori_equipements.find(
        (f) => f.equipement.id === equipementId
      )
      remove({ variables: { id: favId?.id ?? -1 } })
    } else {
      insert({ variables: { data: { societe_id: intervention.fournisseur_id, equipement_id: equipementId } } })
    }
  }, [
    equipementId,
    insert,
    intervention.fournisseur?.fournisseur_favori_equipements,
    intervention.fournisseur_id,
    isFav,
    remove,
  ])

  return (
    <Button
      type="text"
      onClick={handleClick}
      icon={isFav ? <StarFilled style={{ color: COLOR.yellow }} /> : <StarOutlined />}
    />
  )
}

const columns = ({
  typeequipementId,
  equipementId,
  trigramme,
  push,
}: {
  typeequipementId: number
  equipementId: number
  trigramme: string
  push: any
}): ColumnsType<D4_Interventiondemande> => [
  {
    title: 'Date',
    dataIndex: 'datecreationdemande',
    render: (date) => dayjs(date).format(DATE_FORMAT),
    sorter: {
      compare: (a, b) => dayjs(a.datecreationdemande).unix() - dayjs(b.datecreationdemande).unix(),
      multiple: 1,
    },
  },
  {
    title: 'Nb',
    dataIndex: ['fournisseur', 'interventions_aggregate', 'aggregate', 'count'],
    defaultSortOrder: 'descend',
    sorter: {
      compare: (a, b) =>
        //@ts-expect-error
        a.fournisseur.interventions_aggregate.aggregate?.count -
        //@ts-expect-error
        b.fournisseur.interventions_aggregate.aggregate?.count,
      multiple: 2,
    },
  },
  {
    title: 'Fournisseur',
    dataIndex: ['fournisseur', 'raisonsociale'],
  },
  {
    title: 'Tel Fixe',
    dataIndex: ['fournisseur', 'telfixe'],
  },
  {
    title: 'Tel Portable',
    dataIndex: ['fournisseur', 'telportable'],
  },
  {
    title: 'Mail',
    dataIndex: ['fournisseur', 'mail1'],
  },

  {
    title: 'Copro',
    dataIndex: ['demande', 'copropriete', 'trigramme'],
  },
  {
    title: 'Actions',
    render: (_, record) => (
      <div style={{ width: '200px' }}>
        <Button type="link" onClick={() => push({ search: '?demandeId=' + record.demande?.id })}>
          voir la demande
        </Button>
        <FournisseurFavAction
          intervention={record}
          typeequipementId={typeequipementId}
          equipementId={equipementId}
          trigramme={trigramme}
        />
      </div>
    ),
  },
]

const InterventionsAllTable = ({
  interventions,
  typeequipementId,
  equipementId,
  trigramme,
  title,
}: {
  interventions: D4_Interventiondemande[]
  typeequipementId: number
  equipementId: number
  trigramme: string
  title: string
}) => {
  const { push } = useHistory()
  return (
    <Table
      title={() => <Typography.Title level={4}>{title}</Typography.Title>}
      columns={columns({ typeequipementId, equipementId, trigramme, push })}
      dataSource={interventions}
    />
  )
}

export default InterventionsAllTable
